import React, {useState, useEffect, useRef} from 'react'
import {Box, Text, TextProps, Link, HStack, StackProps, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {useViewport, useAnalytics, useHeaderSizeWithoutBanner} from '_customHooks'
import {CategoryMenu, SubCategoryMenu} from '_types'
import {Picture, MenuItem, MenuButton} from '_uiComponents'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getAnalyticsSecondLevel, getLabel, getHierarchy} from '_analytics/utils/analyticsUtils'

interface MixedMenuProps {
    category: CategoryMenu
    menuStaticLibrary: string
    closeMenu: () => void
    tabIndex?: number
}

const MixedMenu = ({category, menuStaticLibrary, closeMenu, tabIndex}: MixedMenuProps): JSX.Element => {
    const {isDesktop, isTablet, isMobile} = useViewport()
    const {locale} = useMultiSite()
    const isTouchEnabled = useRef(false)
    const isHeaderCollapsed = useRef(false)
    const headerSize = useHeaderSizeWithoutBanner(isHeaderCollapsed.current)
    const editorialItems = category.editorial
    const lifeStyleMenu = editorialItems && editorialItems.length > 0
    const catalogImageFive = category.variant === 'catalogImageFive'

    const styles = useMultiStyleConfig('SecondLevelMenu', {
        lifestyle: lifeStyleMenu,
        variant: category.variant,
        headerSize,
    }) as Record<string, StackProps & TextProps>

    const {analyticsEventMenuClick} = useAnalytics()

    const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
    const analyticsTemplate = category.analyticsTemplate || ''
    const mainMenu = category.analytics

    // image trasformation for device it depend on the number of editorial items
    const imageTransformation = {
        default: ['300', '400', '600'],
        3: ['850', '400', '600'],
        4: ['850', '400', '600'],
        5: ['850', '400', '750'],
    }
    const scaleImage = imageTransformation[category?.subcategories?.length ?? 'default'] ?? imageTransformation['default']

    const variantToShow = !isDesktop ? 'header' : 'title'

    const handleCategoryClick = (categoryId: string) => {
        if (categoryId) {
            setSelectedCategory(categoryId === selectedCategory ? null : categoryId)
        }
    }

    const handleBackClick = () => {
        setSelectedCategory(null)
    }

    const imageLinkAnalytics = (analyticsEventData) => {
        analyticsEventMenuClick({
            action: analyticsEventData?.mainMenu || '',
            label: getLabel(analyticsEventData),
            hierarchy: getHierarchy(analyticsEventData),
            menuTemplate: analyticsEventData?.analyticsTemplate || '',
        })
    }

    useEffect(() => {
        if (window && window.matchMedia('(pointer: coarse)').matches) {
            isTouchEnabled.current = true
        }

        if (window) {
            isHeaderCollapsed.current = window.scrollY > 0
        }
    }, [])

    return (
        <>
            <HStack data-testid="mixed-menu" {...styles.mixed}>
                <Box {...styles.mixedCategoryContainer}>
                    {category &&
                        category.subcategories &&
                        category.subcategories.map((subcategory, index) => (
                            <Box {...styles.mixedBox} key={`subcategory-${index}`} data-testid="mixedBox">
                                <Box {...styles.mixedContent} onClick={() => handleCategoryClick(`subcategory-${index}`)}>
                                    <MenuItem
                                        href={subcategory.url || '#'}
                                        variant={!isDesktop ? 'header' : 'title'}
                                        closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                        arrow={isDesktop ? 'none' : 'right'}
                                        tabIndex={tabIndex}
                                        renderaslink={
                                            isDesktop || (catalogImageFive && !isDesktop && subcategory.subcategories && subcategory.subcategories.length <= 1)
                                        }
                                        analyticsEventData={getAnalyticsSecondLevel({
                                            category: subcategory,
                                            subcategory: null,
                                            mainMenu: mainMenu,
                                            analyticsTemplate: analyticsTemplate,
                                            position: 'main',
                                        })}
                                    >
                                        {subcategory.name.toUpperCase()}
                                    </MenuItem>
                                </Box>
                                {!(catalogImageFive && !isDesktop && subcategory.subcategories && subcategory.subcategories?.length <= 1) && (
                                    <Box
                                        {...styles.mixedBoxBis}
                                        {...(selectedCategory === `subcategory-${index}` && !isDesktop ? styles.mixedBoxBisOpen : undefined)}
                                        data-testid="mixedBoxBis"
                                    >
                                        {!isDesktop && (
                                            <Box {...styles.secondLevelBack} onClick={() => handleBackClick()}>
                                                <MenuItem
                                                    href={category.url || '#'}
                                                    variant={!isDesktop ? 'header' : 'title'}
                                                    renderaslink={isDesktop}
                                                    arrow="left"
                                                >
                                                    {/* `as="span"`: MenuItem wraps children in a Chakra Text (<p>); a plain
                                                        Box (<div>) would trigger "div cannot appear as a descendant of p". */}
                                                    <Box as="span" {...styles.subcategory}>{category?.name.toUpperCase() + ' / ' + subcategory?.name.toUpperCase()}</Box>
                                                </MenuItem>
                                                <Box></Box>
                                            </Box>
                                        )}
                                        {subcategory.subcategories?.map((subcategoryBis, indexBis) => (
                                            <Box
                                                {...styles.mixedsubcategoryBis}
                                                {...(indexBis === 0 ? styles.mixedsubcategoryBisBorder : undefined)}
                                                key={`subcategory-${index}-subBis-${indexBis}`}
                                                data-testid="mixedsubcategoryBis"
                                            >
                                                {catalogImageFive && !isDesktop ? (
                                                    <Box
                                                        key={`subcategory-${index}-subBis-${indexBis}-2`}
                                                        {...styles.editorial}
                                                        {...styles.editorialPictureWrapper}
                                                    >
                                                        <Picture
                                                            objectFit="cover"
                                                            position={isMobile || isTablet ? 'absolute' : 'relative'}
                                                            zIndex={0}
                                                            alt={`${category.name} - ${subcategoryBis.name}`}
                                                            imagexs={menuStaticLibrary + subcategoryBis.image_mob + '?sw=' + scaleImage[2] + '&sfrm=jpg'}
                                                            imagelg={menuStaticLibrary + subcategoryBis.image_tab + '?sw=' + scaleImage[1] + '&sfrm=jpg'}
                                                            imagexl={menuStaticLibrary + subcategoryBis.image + '?sw=' + scaleImage[0] + '&sfrm=jpg'}
                                                        />
                                                        <Box {...styles.editorialLinkBox}>
                                                            <MenuButton
                                                                href={subcategoryBis.url || '#'}
                                                                variant={'white'}
                                                                closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                                                analyticsEventData={getAnalyticsSecondLevel({
                                                                    category: subcategory,
                                                                    subcategory: null,
                                                                    mainMenu: mainMenu,
                                                                    analyticsTemplate: analyticsTemplate,
                                                                    position: (indexBis + 1).toString(),
                                                                })}
                                                            >
                                                                {subcategoryBis.name}
                                                            </MenuButton>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <MenuItem
                                                        href={subcategoryBis.url || '#'}
                                                        variant="navigation"
                                                        closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                                        arrow={isDesktop ? 'none' : 'right'}
                                                        tabIndex={tabIndex}
                                                        analyticsEventData={getAnalyticsSecondLevel({
                                                            category: subcategory,
                                                            subcategory: subcategoryBis,
                                                            mainMenu: mainMenu,
                                                            analyticsTemplate: analyticsTemplate,
                                                            position: 'main',
                                                        })}
                                                    >
                                                        {subcategoryBis.name}
                                                    </MenuItem>
                                                )}
                                            </Box>
                                        ))}
                                        {!isDesktop && !catalogImageFive && (
                                            <Box
                                                {...styles.mixedPicture}
                                                {...styles.picturePadding}
                                                as={Link}
                                                href={`/${locale.alias}${subcategory?.url}` || '#'}
                                                onClick={() => {
                                                    const analyticsData = getAnalyticsSecondLevel({
                                                        category: subcategory,
                                                        subcategory: null,
                                                        mainMenu: mainMenu,
                                                        analyticsTemplate: analyticsTemplate,
                                                        position: (index + 1).toString(),
                                                    })
                                                    imageLinkAnalytics(analyticsData)
                                                }}
                                            >
                                                <Box {...styles.mixedPictureBox}>
                                                    <Picture
                                                        objectFit="cover"
                                                        zIndex={1}
                                                        position="absolute"
                                                        alt={`${category.name} - ${subcategory.name}`}
                                                        imagexs={menuStaticLibrary + subcategory.image_mob + '?sw=' + scaleImage[2] + '&sfrm=jpg'}
                                                        imagelg={menuStaticLibrary + subcategory.image_tab + '?sw=' + scaleImage[1] + '&sfrm=jpg'}
                                                        imagexl={menuStaticLibrary + subcategory.image + '?sw=' + scaleImage[0] + '&sfrm=jpg'}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {catalogImageFive && !isDesktop ? undefined : (
                                    <Box
                                        {...styles.mixedPicture}
                                        {...(index > 0 || lifeStyleMenu || catalogImageFive ? styles.picturePadding : undefined)}
                                        as={(subcategory?.url ?? '').indexOf('https://') > -1 ? 'a' : Link}
                                        href={(subcategory?.url ?? '').indexOf('https://') > -1 ? subcategory?.url : `/${locale.alias}${subcategory?.url}`}
                                        onClick={() => {
                                            const analyticsData = getAnalyticsSecondLevel({
                                                category: subcategory,
                                                subcategory: null,
                                                mainMenu: mainMenu,
                                                analyticsTemplate: analyticsTemplate,
                                                position: (index + 1).toString(),
                                            })
                                            imageLinkAnalytics(analyticsData)
                                        }}
                                    >
                                        <Box {...styles.mixedPictureBox}>
                                            <Picture
                                                objectFit="cover"
                                                zIndex={1}
                                                position="absolute"
                                                alt={`${category.name} - ${subcategory.name}`}
                                                imagexs={menuStaticLibrary + subcategory.image_mob + '?sw=' + scaleImage[2] + '&sfrm=jpg'}
                                                imagelg={menuStaticLibrary + subcategory.image_tab + '?sw=' + scaleImage[1] + '&sfrm=jpg'}
                                                imagexl={menuStaticLibrary + subcategory.image + '?sw=' + scaleImage[0] + '&sfrm=jpg'}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        ))}
                </Box>

                {editorialItems && (
                    <Box {...styles.editorialContainer} data-testid="editorial-container">
                        <Box {...styles.catalogEditorialBox}>
                            {editorialItems.map((editorialItem) => (
                                <Box {...styles.editorialWidth} key={editorialItem.cgid}>
                                    <Box {...styles.editorialTitle} {...(variantToShow === 'title' ? styles.linkTextTitle : undefined)}>
                                        <MenuItem
                                            href={editorialItem.url || '#'}
                                            variant={variantToShow}
                                            closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                            analyticsEventData={getAnalyticsSecondLevel({
                                                category: editorialItem,
                                                subcategory: null,
                                                mainMenu: mainMenu,
                                                analyticsTemplate: analyticsTemplate,
                                                position: 'big image box',
                                            })}
                                            tabIndex={tabIndex}
                                        >
                                            <Text as="span" {...styles.subcategory}>
                                                {editorialItem.name.toUpperCase()}
                                            </Text>
                                        </MenuItem>
                                        {!isDesktop && (
                                            <MenuButton
                                                href={editorialItem.url || '#'}
                                                variant={'underline'}
                                                closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                                analyticsEventData={getAnalyticsSecondLevel({
                                                    category: editorialItem,
                                                    subcategory: null,
                                                    mainMenu: mainMenu,
                                                    analyticsTemplate: analyticsTemplate,
                                                    position: 'big image box',
                                                })}
                                            >
                                                <FormattedMessage id="label.discover" defaultMessage="Discover More" />
                                            </MenuButton>
                                        )}
                                    </Box>
                                    {!isDesktop && (
                                        <Box
                                            {...styles.editorialPitcure}
                                            as={(editorialItem?.url ?? '').indexOf('https://') > -1 ? 'a' : Link}
                                            href={
                                                (editorialItem?.url ?? '').indexOf('https://') > -1
                                                    ? editorialItem?.url
                                                    : `/${locale.alias}${editorialItem?.url}`
                                            }
                                            onClick={() => {
                                                const analyticsEventData = getAnalyticsSecondLevel({
                                                    category: editorialItem,
                                                    subcategory: null,
                                                    mainMenu: mainMenu,
                                                    analyticsTemplate: analyticsTemplate,
                                                    position: 'big image box',
                                                })
                                                analyticsEventMenuClick({
                                                    action: analyticsEventData?.mainMenu || '',
                                                    label: getLabel(analyticsEventData),
                                                    hierarchy: getHierarchy(analyticsEventData),
                                                    menuTemplate: analyticsEventData?.analyticsTemplate || '',
                                                })
                                            }}
                                        >
                                            <Picture
                                                objectFit="cover"
                                                zIndex={1}
                                                position="absolute"
                                                imagexs={
                                                    menuStaticLibrary +
                                                    (lifeStyleMenu ? editorialItem.image : editorialItem.image_mob) +
                                                    '?sw=' +
                                                    scaleImage[2] +
                                                    '&sfrm=jpg'
                                                }
                                                imagelg={
                                                    menuStaticLibrary +
                                                    (lifeStyleMenu ? editorialItem.image : editorialItem.image_tab) +
                                                    '?sw=' +
                                                    scaleImage[1] +
                                                    '&sfrm=jpg'
                                                }
                                                imagexl={menuStaticLibrary + editorialItem.image + '?sw=' + scaleImage[0] + '&sfrm=jpg'}
                                                alt={`${category.name} - ${editorialItem.name}`}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                        {isDesktop && (
                            <Box {...styles.catalogEditorialBox}>
                                {editorialItems.map((editorialItem) => (
                                    <Box {...styles.editorialWidth} key={'pitc-' + editorialItem.cgid ? editorialItem.cgid : editorialItem.url}>
                                        <Box
                                            {...styles.editorialPitcure}
                                            as={(editorialItem?.url ?? '').indexOf('https://') > -1 ? 'a' : Link}
                                            href={
                                                (editorialItem?.url ?? '').indexOf('https://') > -1
                                                    ? editorialItem?.url
                                                    : `/${locale.alias}${editorialItem?.url}`
                                            }
                                            onClick={() => {
                                                const analyticsEventData = getAnalyticsSecondLevel({
                                                    category: editorialItem,
                                                    subcategory: null,
                                                    mainMenu: mainMenu,
                                                    analyticsTemplate: analyticsTemplate,
                                                    position: 'big image box',
                                                })
                                                analyticsEventMenuClick({
                                                    action: analyticsEventData?.mainMenu || '',
                                                    label: getLabel(analyticsEventData),
                                                    hierarchy: getHierarchy(analyticsEventData),
                                                    menuTemplate: analyticsEventData?.analyticsTemplate || '',
                                                })
                                            }}
                                        >
                                            <Picture
                                                objectFit="cover"
                                                zIndex={1}
                                                position="absolute"
                                                imagexs={menuStaticLibrary + editorialItem.image_mob + '?sw=' + scaleImage[2] + '&sfrm=jpg'}
                                                imagelg={
                                                    menuStaticLibrary +
                                                    (lifeStyleMenu ? editorialItem.image : editorialItem.image_tab) +
                                                    '?sw=' +
                                                    scaleImage[1] +
                                                    '&sfrm=jpg'
                                                }
                                                imagexl={menuStaticLibrary + editorialItem.image + '?sw=' + scaleImage[0] + '&sfrm=jpg'}
                                                alt={`${category.name} - ${editorialItem.name}`}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {isDesktop && (
                            <Box {...styles.catalogEditorialBox}>
                                {editorialItems.map((editorialItem) => (
                                    <Box {...styles.editorialWidth} key={'link-' + editorialItem.cgid ? editorialItem.cgid : editorialItem.url}>
                                        <Box {...styles.hiddenforMobile}>
                                            <MenuItem
                                                href={editorialItem.url || '#'}
                                                variant={'navigation'}
                                                closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                                analyticsEventData={getAnalyticsSecondLevel({
                                                    category: editorialItem,
                                                    subcategory: null,
                                                    mainMenu: mainMenu,
                                                    analyticsTemplate: analyticsTemplate,
                                                    position: 'big image box',
                                                })}
                                                tabIndex={tabIndex}
                                            >
                                                <Text as="span" {...styles.discoverMoreDesktop}>
                                                    <FormattedMessage id="label.discover" defaultMessage="Discover More" />
                                                </Text>
                                            </MenuItem>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </HStack>
            <Box {...styles.catalogShowAll}>
                {category.url && category.show_all && (
                    <MenuButton
                        href={category.url || '#'}
                        variant={'footer'}
                        closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                        analyticsEventData={getAnalyticsSecondLevel({
                            category: category,
                            subcategory: null,
                            mainMenu: mainMenu,
                            analyticsTemplate: analyticsTemplate,
                            position: 'footer',
                        })}
                    >
                        {category.show_all.toUpperCase()}
                    </MenuButton>
                )}
            </Box>
        </>
    )
}

export default MixedMenu
